import dayjs, { Dayjs } from 'dayjs';
import { iconComponents } from '../components/Core/IconImports';
import { FileLimitationEnum } from '../enums/FileLimitationEnum';
import { FileTypeEnum } from '../enums/FileTypeEnum';
import { InterfaceLimitationEnum } from '../enums/InterfaceLimitationEnum';
import { SpecialFolderEnum } from '../enums/SpecialFolderEnum';
import { UserLimitationEnum } from '../enums/UserLimitationEnum';
import { UserRoleEnum } from '../enums/UserRoleEnum';
import type File from '../interfaces/File';
import type FileLimitation from '../interfaces/FileLimitation';
import type InterfaceLimitation from '../interfaces/InterfaceLimitation';
import type User from '../interfaces/User';
import type UserLimitation from '../interfaces/UserLimitation';
import { translate } from '../utils/translation';

// Constants for ticks conversion
const EPOCH_OFFSET = 621355968000000000;
const TICKS_PER_MS = 10000;

/**
 * Converts a JavaScript Date object to ticks.
 *
 * @param {Date} date - The date object to convert.
 * @return {number} The date in ticks.
 */
export function dateToTicks(date: Date): number {
  // var d = new Date(); // DateTime.Now
  // d = new Date(d.getTime() - d.getTimezoneOffset() * 60000)    // DateTime.UtcNow

  const ticks = date.getTime() * TICKS_PER_MS + EPOCH_OFFSET;

  return ticks;
}

/**
 * Converts ticks to a Date object.
 *
 * @param {number} ticks - The ticks to convert.
 * @return {Date} The Date object.
 */
export function dateFromTicks(ticks: number) {
  const millisecond = (ticks - EPOCH_OFFSET) / TICKS_PER_MS;

  return new Date(millisecond);
}

/**
 * Converts ticks to milliseconds.
 *
 * @param {number} ticks - The ticks to convert.
 * @return {number} The milliseconds.
 */
export function ticksToMs(ticks: number): number {
  return (ticks - EPOCH_OFFSET) / TICKS_PER_MS;
}

/**
 * Converts ticks to a Dayjs object.
 *
 * @param {number} ticks - The ticks to convert.
 * @return {Dayjs} The Dayjs object.
 */
export function ticksToDayjs(ticks: number): Dayjs {
  return dayjs.utc(ticksToMs(ticks));
}

/**
 * Converts milliseconds to ticks.
 *
 * @param {number} ms - The milliseconds to convert.
 * @return {number} The ticks.
 */
export function msToTicks(ms: number): number {
  return ms * TICKS_PER_MS + EPOCH_OFFSET;
}

/**
 * Converts UTC ticks to a local Dayjs object.
 *
 * @param {number} ticks - The ticks to convert.
 * @return {Dayjs} The local Dayjs object.
 */
export function localDateFromUtcTicks(ticks: number): Dayjs {
  const msUtc = ticksToMs(ticks); // ms since epoch UTC
  const dtUtc = dayjs.utc(msUtc); // Dayjs UTC at that instant
  const year = dtUtc.year();
  const month = dtUtc.month() + 1; // month is 0-indexed
  const day = dtUtc.date();
  return dayjs(`${year}-${month}-${day}`, 'YYYY-M-D');
}

/**
 * @desc Get logged user icon
 * @param user
 * @return {string}
 */
export const getUserIcon = (user: User): keyof typeof iconComponents => {
  switch (user?.role) {
    case 1001:
      return 'AdminIcon';
    case 2001:
      return 'ContributorIcon';
    case 3002:
      return 'ReaderIcon';
    default:
      return 'ReaderIcon';
  }
};

/**
 * Determines the color representing a user's status based on their status value and expiry.
 *
 * @param {0 | 1} status - The status of the user. A value of 0 represents an active user, while 1 represents an inactive user.
 * @param {number} expiry - The expiration timestamp in ticks. A value greater than 0 is considered for expiry checking.
 * @return {string} The color code representing the user's status ('green' for active users without expiry or '#EE3333' otherwise).
 */
export function getUserStatusColor(isExpired: boolean): string {
  if (!isExpired) return '#008036';
  else return '#AB0D02';
}

/**
 * Returns the status text of a user based on their status code and expiry time.
 *
 * @param {number} status - The status of the user (e.g., 0 for active, 1 for blocked).
 * @param {number} expiry - The expiry timestamp of the user's status in ticks.
 * @return {string} The translated status text ('active', 'blocked', or 'expired').
 */
export function getUserStatusText(user: User): string {
  if (user.isBlocked) return translate('blocked');
  else if (user.isExpired) return translate('expired');
  else return translate('active');
}

/**
 * Retrieves the icon name corresponding to the specified user role.
 *
 * @param {UserRoleEnum} role - The role of the user for which the icon name is required.
 * @return {string} The name of the icon associated with the provided user role.
 */
export function getUserRoleIconName(role: UserRoleEnum): keyof typeof iconComponents {
  switch (role) {
    case UserRoleEnum.Admin:
      return 'AdminIcon';
    case UserRoleEnum.Contributor:
      return 'ContributorIcon';
    case UserRoleEnum.Reader:
      return 'ReaderIcon';
    default:
      return 'ReaderIcon';
  }
}

/**
 * Converts an ArrayBuffer to a Base64 encoded string.
 *
 * @param {ArrayBuffer} buffer - The ArrayBuffer to convert.
 * @return {string} The Base64 encoded string representation of the ArrayBuffer.
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return window.btoa(binary);
}

/**
 * Generates a hash from the provided file bytes using the SHA-256 algorithm.
 * The hash can be formatted as a hexadecimal string or a base64 string.
 *
 * @param {ArrayBuffer | Uint8Array} fileBytes - The file bytes to hash.
 * @param {boolean} formatHex - Whether to format the hash as a hexadecimal string.
 * @return {Promise<string>} A promise that resolves to the hash string.
 */
export async function getHashFromBytes(
  fileBytes: ArrayBuffer | Uint8Array,
  formatHex: boolean,
): Promise<string> {
  const buffer = await crypto.subtle.digest('SHA-256', fileBytes);
  if (formatHex) {
    const hashArray = Array.from(new Uint8Array(buffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  } else {
    return arrayBufferToBase64(buffer);
  }
}

/**
 * Asynchronously generates a unique hash for the company's origin URL.
 * Utilizes a TextEncoder to encode the window's origin URL into a Uint8Array,
 * then computes and returns a hash based on this encoded data.
 *
 * @return {Promise<string>} A promise that resolves to a string representing the hash
 */
export async function getCompanyHash(): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(window.location.origin);
  return await getHashFromBytes(data, true);
}

/**
 * Generates a UUID (Universally Unique Identifier) string.
 * The generated UUID is a 36 character string in the format XXXXXXXX-XXXX-4XXX-YXXX-XXXXXXXXXXXX, where X is any hexadecimal digit and Y is one of 8, 9, A, or B.
 *
 * @return {string} A randomly generated UUID.
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Returns the base URL for editing office files based on the specified file type.
 *
 * @param {FileTypeEnum} fileType - The type of the office file (Word, Excel, Powerpoint, Wopitest).
 * @return {string} The base URL for the specified office file type.
 */
export function getOfficeFileBaseUrl(fileType: FileTypeEnum): string {
  switch (fileType) {
    case FileTypeEnum.Word:
      return `${import.meta.env.VITE_APP_OFFICE_WORD_URL}/we/wordeditorframe.aspx?`;
    case FileTypeEnum.Excel:
      return `${import.meta.env.VITE_APP_OFFICE_EXCEL_URL}/x/_layouts/xlviewerinternal.aspx?edit=1&`;
    case FileTypeEnum.Powerpoint:
      return `${import.meta.env.VITE_APP_OFFICE_POWERPOINT_URL}/p/PowerPointFrame.aspx?PowerPointView=EditView&`;
    case FileTypeEnum.Wopitest:
      return `${import.meta.env.VITE_APP_OFFICE_WOPITEST_URL}/hosting/WopiTestFrame.aspx?`;
    default:
      return '';
  }
}

/**
 * Retrieves the favicon URL for a given office file type.
 *
 * @param {FileTypeEnum} fileType - The type of the office file.
 * @return {string} The URL of the favicon corresponding to the specified file type.
 */
export function getOfficeFileFavicon(fileType: FileTypeEnum): string {
  switch (fileType) {
    case FileTypeEnum.Word:
      return import.meta.env.VITE_APP_OFFICE_WORD_ICON_URL;
    case FileTypeEnum.Excel:
      return import.meta.env.VITE_APP_OFFICE_EXCEL_ICON_URL;
    case FileTypeEnum.Powerpoint:
      return import.meta.env.VITE_APP_OFFICE_POWERPOINT_ICON_URL;
    case FileTypeEnum.Wopitest:
      return import.meta.env.VITE_APP_OFFICE_WOPITEST;
    default:
      return '';
  }
}

/**
 * Converts a given text string into a URL-friendly slug.
 *
 * @param {string} text - The input string to be slugified.
 * @return {string} The slugified version of the input string.
 */
export function slugify(text: string): string {
  return text
    .toString()
    .normalize('NFD') // Normalize to decompose diacritics (e.g. é -> e)
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .toLowerCase()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[_\-]+/g, '-') // Replace underscores and multiple dashes with a single dash
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
}

/**
 * Copies the given text to the clipboard using the navigator.clipboard API if available,
 * otherwise falls back to a manual method.
 *
 * @param {string} text - The text to be copied to the clipboard.
 * @return {Promise<void>} A promise that resolves when the text is copied successfully.
 */
export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  } else {
    return new Promise((resolve, reject) => {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // Impostazioni per evitare che il textarea influenzi lo scorrimento della pagina
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          resolve();
        } else {
          reject(new Error('Copia negli appunti fallita con il metodo fallback.'));
        }
      } catch (err) {
        reject(err);
      } finally {
        document.body.removeChild(textArea);
      }
    });
  }
}

/**
 * Determines a hexadecimal color code based on the properties of the provided file.
 *
 * @param {File} file - The file object which includes a property 'isQuickShare' that determines the returned color code.
 * @return {string} A hexadecimal color code. Returns '#193057' if the file is marked as 'isQuickShare', otherwise '#FFB900'.
 */
export function getFolderHex(file: File): string {
  return file?.isQuickShare ? '#193057' : '#FFB900';
}

/**
 * Determines if the given file corresponds to a special folder based on its title.
 *
 * @param {File} file - The file object to check.
 * @return {boolean} Returns true if the file's title matches any special folder in the SpecialFolderEnum, otherwise false.
 */
export function isFileSpecialFolder(file: File): boolean {
  if (!file || !file.fileName) {
    return false;
  }

  return Object.values(SpecialFolderEnum).includes(file.fileName as SpecialFolderEnum);
}

/**
 * Retrieves the title of a file. If the file's title matches a special folder enumeration,
 * it translates and returns the corresponding folder name. If the title is not defined, it
 * returns a default value.
 *
 * @param {File} file - The file object containing the title to be retrieved.
 * @return {string} The title of the file, translated if it's a special folder,
 * or a default value if undefined.
 */
export function getFileTitle(file: File): string {
  if (
    file?.fileName &&
    Object.values(SpecialFolderEnum).includes(file.fileName as SpecialFolderEnum)
  ) {
    return translate(`specialFolders.${file.fileName}`);
  } else {
    return file?.fileName || 'N/D';
  }
}

/**
 * Fetches the default image URL for a specified branding context.
 *
 * @param {'email' | 'quick-share' | 'login-page' | 'top-bar'} image - The specific branding context for which the default image URL is required.
 * @return {string} The URL of the default image corresponding to the provided branding context. Returns an empty string if no match is found.
 */
export function getBrandingDataDefaultImage(
  image: 'email' | 'quick-share' | 'login-page' | 'top-bar',
) {
  const baseUrl = `${import.meta.env.VITE_APP_AZURE_STORAGE_URL}/${import.meta.env.VITE_APP_AZURE_RESOURCES_CONTAINER}/`;

  switch (image) {
    case 'email':
      return `${baseUrl}emailNotificationLogo.png?v=${Date.now()}`;
    case 'quick-share':
      return `${baseUrl}quickShare.webp?v=${Date.now()}`;
    case 'login-page':
      return `${baseUrl}loginPageLogo.svg?v=${Date.now()}`;
    case 'top-bar':
      return `${baseUrl}topBarLogo.svg?v=${Date.now()}`;
    default:
      return '';
  }
}

/**
 * Parses the given file name and removes the .shortcut extension if it is present.
 *
 * @return {string} The modified file name without the .shortcut extension if it was present.
 * @param title
 */
export function removeShortcutExtension(title: string): string {
  // remove .shortuct extension if present
  const extensionIndex = title.lastIndexOf('.shortcut');
  if (extensionIndex !== -1) {
    title = title.substring(0, extensionIndex);
  }
  return title;
}

/**
 * Validates whether a given string is a properly formatted email address.
 *
 * @param email - The email address to validate.
 * @returns `true` if the email is valid, `false` otherwise.
 *
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Generates a purchase link for a given file ID.
 *
 * @param {number} fileId - The ID of the file to generate a purchase link for.
 * @return {string} The generated purchase link.
 */
export function getPurchaseLink(fileId: number): string {
  return document.location.origin + '/store/' + fileId;
}

/**
 * Prints the currency symbol for a given currency code.
 *
 * @param {string} currency - The currency code to print.
 * @return {string} The currency symbol.
 */
export function printCurrency(currency: string) {
  switch (currency) {
    case 'EUR':
      return '€';
    case 'USD':
      return '$';
    default:
      return currency;
  }
}

/**
 * Formats a given amount with optional currency and locale settings.
 *
 * @param {number} amount - The amount to be formatted.
 * @param {Object} options - Optional configuration object.
 * @param {string} options.locale - The locale to use for formatting.
 * @param {string} options.currency - The currency to use for formatting.
 * @return {string} The formatted amount.
 */
export function printAmount(
  amount: number,
  options?: {
    locale?: string;
    currency?: string;
  },
): string {
  const locale = options?.locale || navigator.language;
  const currency = options?.currency;

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...(currency && { style: 'currency', currency }),
  }).format(amount);
}

export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Checks if a file has a specific limitation type.
 *
 * @param {FileLimitation[]} fileLimitations - The limitations of the file.
 * @param {FileLimitationEnum} type - The type of limitation to check for.
 * @return {boolean} Returns true if the file has the specified limitation, false otherwise.
 */
export function hasFileLimitation(
  fileLimitations: FileLimitation[],
  type: FileLimitationEnum,
): boolean {
  return fileLimitations.some((limitation) => limitation.name === type);
}

/**
 * Checks if a user has a specific limitation type.
 *
 * @param {UserLimitation[]} userLimitations - The limitations of the user.
 * @param {UserLimitationEnum} type - The type of limitation to check for.
 * @return {boolean} Returns true if the user has the specified limitation, false otherwise.
 */
export function hasUserLimitation(
  userLimitations: UserLimitation[],
  type: UserLimitationEnum,
): boolean {
  return userLimitations.some((limitation) => limitation.name === type);
}

/**
 * Checks if a user has a specific limitation type.
 *
 * @param {InterfaceLimitation[]} interfaceLimitations - The limitations of the user.
 * @param {InterfaceLimitationEnum} type - The type of limitation to check for.
 * @return {boolean} Returns true if the user has the specified limitation, false otherwise.
 */
export function hasInterfaceLimitation(
  interfaceLimitations: InterfaceLimitation[],
  type: InterfaceLimitationEnum,
): boolean {
  return interfaceLimitations.some((limitation) => limitation.name === type);
}

/**
 * Sets a nested value in an object using a dot-separated path.
 *
 * @param {T} obj - The object to modify.
 * @param {string} path - The dot-separated path to the value to set.
 * @param {any} value - The value to set.
 * @return {T} The modified object.
 */
export function setObjectNestedValue<T extends object>(obj: T, path: string, value: any): T {
  const keys = path.split('.');
  const newObj = { ...obj } as any;
  let curr = newObj;

  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    if (k === undefined) continue;
    curr[k] = curr[k] ? { ...curr[k] } : {};
    curr = curr[k];
  }

  const lastKey = keys[keys.length - 1];
  if (lastKey !== undefined) {
    curr[lastKey] = value;
  }
  return newObj as T;
}

/**
 * Checks if a date in .NET ticks is in the past compared to now (UTC).
 * @param ticks - Date in .NET ticks (UTC)
 * @returns {boolean} True if the date is in the past, false if in the future or not set/invalid
 */
export function isTicksBeforeNow(ticks?: number | null): boolean {
  if (!ticks || ticks <= 0) return false;
  return ticksToMs(ticks) < Date.now();
}

/**
 * Returns the time since a given date.
 *
 * @param date - The date to calculate the time since.
 * @returns The time since the date.
 */
export function timeSince(date: Date | number): string {
  // Supporta sia Date che timestamp numerico
  const now = new Date();
  const givenDate = typeof date === 'number' ? new Date(date) : date;
  const seconds = Math.floor((now.getTime() - givenDate.getTime()) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('years_ago');
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('months_ago');
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('days_ago');
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('hours_ago');
  }
  interval = seconds / 60;
  if (interval > 1) {
    return Math.floor(interval) + ' ' + translate('minutes_ago');
  }

  return Math.floor(seconds + 1) + ' ' + translate('seconds_ago');
}

/**
 * Formats a given number of bytes into a human-readable string.
 *
 * @param {number} bytes - The number of bytes to format.
 * @return {string} The formatted string representing the number of bytes in a human-readable format.
 */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${bytes} B`;
}

/**
 * Lightens a hex color by a given percentage.
 *
 * @param {string} hex - The hex color to lighten.
 * @param {number} percent - The percentage to lighten the color by.
 * @return {string} The lightened hex color.
 */
export function lightenHex(hex: string, percent = 10): string {
  // Gestisce anche formati #abc o #aabbcc
  let color = hex.replace(/^#/, '');

  if (color.length === 3) {
    color = color
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = parseInt(color, 16);

  let r = (num >> 16) + Math.round(2.55 * percent);
  let g = ((num >> 8) & 0x00ff) + Math.round(2.55 * percent);
  let b = (num & 0x0000ff) + Math.round(2.55 * percent);

  r = Math.max(Math.min(255, r), 0);
  g = Math.max(Math.min(255, g), 0);
  b = Math.max(Math.min(255, b), 0);

  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
